import React from 'react';
import { FilterState } from '../hooks/useFilterState';

interface MultiSelectConfig {
  label: string;
  options: { value: string; label: string }[];
}

export interface FilterConfig {
  dateRange?: {
    label: string;
    enabled: boolean;
  };
  multiSelect?: Record<string, MultiSelectConfig>;
}

interface AdvancedFiltersProps {
  isOpen: boolean;
  onToggle: () => void;
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  onClearFilters: () => void;
  filterConfig: FilterConfig;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  isOpen,
  onToggle,
  filters,
  onFiltersChange,
  onClearFilters,
  filterConfig,
}) => {
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...(filters.dateRange || { startDate: null, endDate: null }),
        [field]: value || null,
      },
    });
  };

  const toggleMultiSelect = (key: string, optionValue: string) => {
    const currentSelections = filters.multiSelect?.[key] || [];
    const exists = currentSelections.includes(optionValue);
    const updatedSelections = exists
      ? currentSelections.filter((value) => value !== optionValue)
      : [...currentSelections, optionValue];

    const nextMultiSelect = {
      ...(filters.multiSelect || {}),
      [key]: updatedSelections,
    };

    if (nextMultiSelect[key].length === 0) {
      delete nextMultiSelect[key];
    }

    onFiltersChange({
      ...filters,
      multiSelect: nextMultiSelect,
    });
  };

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Advanced Filters</h3>
          <p className="text-xs text-gray-500">Refine the dataset with multi-select filters</p>
        </div>
        <button
          onClick={onToggle}
          className="text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          {isOpen ? 'Hide filters' : 'Show filters'}
        </button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-6">
          {filterConfig.dateRange?.enabled && (
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">
                {filterConfig.dateRange.label} (Start)
                <input
                  type="date"
                  value={filters.dateRange?.startDate ?? ''}
                  onChange={(event) => handleDateChange('startDate', event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                {filterConfig.dateRange.label} (End)
                <input
                  type="date"
                  value={filters.dateRange?.endDate ?? ''}
                  onChange={(event) => handleDateChange('endDate', event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            </div>
          )}

          {filterConfig.multiSelect &&
            Object.entries(filterConfig.multiSelect).map(([key, config]) => (
              <div key={key}>
                <p className="text-sm font-semibold text-gray-800 mb-2">{config.label}</p>
                <div className="flex flex-wrap gap-2">
                  {config.options.map((option) => {
                    const isActive = filters.multiSelect?.[key]?.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        onClick={() => toggleMultiSelect(key, option.value)}
                        className={`rounded-full px-3 py-1 text-sm ${
                          isActive
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          <div className="flex justify-end gap-2">
            <button
              onClick={onClearFilters}
              className="rounded border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface ActiveFiltersProps {
  filters: FilterState;
  filterConfig: FilterConfig;
  onRemoveFilter: (key: string, value?: string) => void;
}

export const ActiveFiltersDisplay: React.FC<ActiveFiltersProps> = ({
  filters,
  filterConfig,
  onRemoveFilter,
}) => {
  const chips: Array<{ key: string; label: string; value?: string }> = [];

  if (filters.dateRange?.startDate || filters.dateRange?.endDate) {
    const label = filterConfig.dateRange?.label || 'Date Range';
    chips.push({
      key: 'dateRange',
      label: `${label}: ${filters.dateRange.startDate ?? 'Any'} → ${filters.dateRange.endDate ?? 'Any'}`,
    });
  }

  if (filters.multiSelect) {
    Object.entries(filters.multiSelect).forEach(([key, selections]) => {
      const configLabel = filterConfig.multiSelect?.[key]?.label ?? key;
      selections.forEach((selection) => {
        const optionLabel =
          filterConfig.multiSelect?.[key]?.options.find((option) => option.value === selection)
            ?.label ?? selection;

        chips.push({
          key,
          value: selection,
          label: `${configLabel}: ${optionLabel}`,
        });
      });
    });
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {chips.map((chip, index) => (
        <span
          key={`${chip.key}-${chip.value ?? index}`}
          className="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800"
        >
          {chip.label}
          <button
            className="text-blue-600 hover:text-blue-900"
            onClick={() => onRemoveFilter(chip.key, chip.value)}
            aria-label="Remove filter"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};
